import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { NewSkillModal } from "../components/NewSkillModal.tsx";
import { api, type SkillWithUsage } from "../lib/api.ts";

/**
 * Skills directory page. Lists every skill in the registry — builtin first,
 * external second. Each card shows kind, origin, and how many agents currently
 * use it. Click → /skills/:key to edit; `+ New skill` opens NewSkillModal which
 * POSTs and shows the resulting PR URL.
 */
export function Skills() {
	const [skills, setSkills] = useState<SkillWithUsage[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [showNew, setShowNew] = useState(false);

	function refresh() {
		api
			.listSkills()
			.then((r) => setSkills(r.skills))
			.catch((err) => setError((err as Error).message));
	}

	useEffect(refresh, []);

	const grouped = useMemo(() => {
		const builtin: SkillWithUsage[] = [];
		const external: SkillWithUsage[] = [];
		for (const s of skills ?? []) {
			if (s.origin === "builtin") builtin.push(s);
			else external.push(s);
		}
		const cmp = (a: SkillWithUsage, b: SkillWithUsage) => a.displayName.localeCompare(b.displayName);
		builtin.sort(cmp);
		external.sort(cmp);
		return { builtin, external };
	}, [skills]);

	return (
		<div className="px-6 py-8 max-w-4xl mx-auto">
			<div className="flex items-baseline justify-between mb-6">
				<div>
					<h1 className="text-xl font-semibold">Skills</h1>
					<p className="text-sm text-zinc-500 mt-1">
						{skills === null
							? "Loading…"
							: `${grouped.builtin.length} built-in · ${grouped.external.length} external · ${skills.length} total`}
					</p>
				</div>
				<button
					type="button"
					onClick={() => setShowNew(true)}
					className="text-sm px-3 py-1.5 rounded border border-zinc-700 hover:border-zinc-500 text-zinc-200 hover:bg-zinc-800/40"
				>
					+ New skill
				</button>
			</div>

			{showNew && (
				<NewSkillModal
					onClose={() => {
						setShowNew(false);
						refresh();
					}}
				/>
			)}

			{error && (
				<div className="p-3 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300 mb-4">
					{error}
				</div>
			)}

			{grouped.builtin.length > 0 && (
				<section className="mb-8">
					<h2 className="text-sm font-medium text-zinc-400 mb-3">Built-in (curated)</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						{grouped.builtin.map((s) => (
							<SkillCard key={s.key} s={s} />
						))}
					</div>
				</section>
			)}

			{grouped.external.length > 0 && (
				<section>
					<h2 className="text-sm font-medium text-zinc-400 mb-3">External</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						{grouped.external.map((s) => (
							<SkillCard key={s.key} s={s} />
						))}
					</div>
				</section>
			)}

			{skills !== null && skills.length === 0 && (
				<div className="text-sm text-zinc-500 italic">
					No skills yet. Add one to give agents access to MCP servers or Claude Code plugins.
				</div>
			)}
		</div>
	);
}

function SkillCard({ s }: { s: SkillWithUsage }) {
	return (
		<Link
			to={`/skills/${encodeURIComponent(s.key)}`}
			className="border border-zinc-800 rounded-lg bg-zinc-900/40 p-4 flex flex-col gap-2 hover:border-zinc-700 hover:bg-zinc-900/60 transition-colors"
		>
			<div className="flex items-baseline justify-between gap-3">
				<div className="min-w-0">
					<div className="text-sm font-semibold text-zinc-100">{s.displayName}</div>
					<div className="text-[11px] text-zinc-500 font-mono">{s.key}</div>
				</div>
				<KindBadge kind={s.kind} />
			</div>
			{s.description && <div className="text-xs text-zinc-400 line-clamp-2">{s.description}</div>}
			<div className="text-[11px] text-zinc-500 flex gap-2 mt-1">
				<span>{s.usageCount === 0 ? "unused" : `used by ${s.usageCount}`}</span>
				{s.containsSecret && <span className="text-amber-400">contains secret</span>}
			</div>
		</Link>
	);
}

function KindBadge({ kind }: { kind: SkillWithUsage["kind"] }) {
	const map: Record<SkillWithUsage["kind"], { label: string; cls: string }> = {
		"mcp-http": { label: "MCP HTTP", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
		"mcp-stdio": { label: "MCP stdio", cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" },
		plugin: { label: "Plugin", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
		"marketplace-plugin": {
			label: "Marketplace",
			cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
		},
	};
	const { label, cls } = map[kind];
	return <span className={`text-[10px] px-2 py-0.5 rounded border ${cls} whitespace-nowrap`}>{label}</span>;
}
